import type {
  Asset,
  BenefitPlan,
  Course,
  EmployeeLoan,
  EmployeeRelationsCase,
  Money,
  OffboardingCase,
  OffboardingReason,
  OnboardingCase,
  PerformanceReview,
  ProbationReview,
  BenefitEnrollment,
  ReviewCycle,
  TrainingRecord,
} from '@micarahr/shared-types';
import { newIdempotencyKey } from '@micarahr/api-client';
import { moneyFromDecimalStringOrZero } from '@micarahr/shared-utils';
import { apiTransport } from './api-client';
import { documentStatus } from './document-status';
import { paginate, type Page, type PageRequest } from './collections';
import type {
  AnnouncementRecord,
  BenefitEnrollmentRow,
  EmployeeDocumentRecord,
  LeaveBalanceRecord,
  LeaveType,
  PolicyRecord,
} from './modules';
import type { WorkflowTask } from '@micarahr/shared-types';
import type { AuditActor } from './audit';
import {
  narrow,
  narrowDescriptive,
  ASSET_CONDITIONS,
  ASSET_STATUSES,
  LOAN_STATUSES,
  OFFBOARDING_REASONS,
  PROBATION_DECISIONS,
  REVIEW_STATUSES,
  TASK_OWNER_KINDS,
  TASK_STATUSES,
} from './narrow';
import { addDaysToKey, businessDateOf, todayInBusiness } from '../lib/business-date';
import { offboardingReasonLabel } from '../lib/people-labels';

/**
 * Read paths for the remaining HR modules.
 *
 * Cases (onboarding, probation, offboarding, performance), registries
 * (documents, policies, announcements, assets, courses), payroll-adjacent
 * records (benefits, loans) and employee relations.
 *
 * Reads and the write paths the server actually routes. The prototype's write
 * signatures carry an audit actor and sometimes a permission list — things a
 * server resolves from the session — so the adapters here accept those
 * arguments to keep the mock's exact shape and simply do not send them. A
 * method whose server surface does not exist stays on the mock, with a
 * one-line comment at the selector naming the missing route.
 *
 * Employee relations is the exception worth naming. Its list is fetched like
 * any other, but the server decides per caller whether `confidentialNotes` is
 * present at all — absent, not blanked — and every read of it is audited with
 * the reader's address. Nothing here weakens that; the adapter simply passes
 * through what the server chose to send.
 *
 * ---
 *
 * **Prepared, not yet wired.**
 *
 * Each module's screens read a distinct row type, so connecting one means
 * writing its mapper — the API nests relations where the screens want them
 * flat, exactly as the directory and attendance did. A generic reader returning
 * `Record<string, unknown>` cannot be spread over a typed mock without widening
 * every return, which is the trade this integration has refused throughout:
 * failures belong at build time, not at the moment somebody opens a page.
 *
 * Every module now has a mapper written against the shape its endpoint actually
 * returns. The generic reader that stood in for them is gone: it produced
 * `Record<string, unknown>`, which cannot be spread over a typed mock without
 * widening every return — the trade this integration has refused throughout,
 * because failures belong at build time rather than at the moment somebody
 * opens a page.
 */

/**
 * A written record, re-read.
 *
 * Several transition routes answer with the bare row — no employee join, no
 * tasks — while the screens render the joined case, so the adapters re-read
 * the list after a successful write. If the row has since left the list (the
 * lists are capped server-side), the write still happened; the error says so
 * rather than reporting a failure that did not occur.
 */
function found<T>(row: T | undefined | null, what: string): T {
  if (row === undefined || row === null) {
    throw new Error(
      `The change was saved, but the ${what} could not be re-read. Refresh the page to see it.`,
    );
  }
  return row;
}

interface ApiWorkflowTask {
  id: string;
  title: string;
  description: string | null;
  ownerKind: string;
  ownerName: string | null;
  status: string;
  dueDate: string | null;
  isBlocking: boolean;
  completedAt: string | null;
  notes: string | null;
  waitingOnAssets?: { assetCode: string; name: string }[];
  waitingOnLoans?: { referenceCode: string; loanType: string; balance: string }[];
  accessRevocation?: WorkflowTask['accessRevocation'];
}

/**
 * One checklist task, onboarding or offboarding.
 *
 * Both cases carry the same tasks and the same mapping, so it lives once. The
 * `completedByName` is null because the server records `completedByUserId`
 * and this projection does not resolve it to a person — a uuid in a column
 * labelled "completed by" is worse than an em dash.
 */
function toWorkflowTask(task: ApiWorkflowTask): WorkflowTask {
  return {
    id: task.id,
    title: task.title,
    description: task.description,
    ownerKind: narrow(TASK_OWNER_KINDS, task.ownerKind, 'task owner'),
    ownerName: task.ownerName,
    dueDate: task.dueDate ? task.dueDate.slice(0, 10) : null,
    isBlocking: task.isBlocking,
    status: narrow(TASK_STATUSES, task.status, 'task status'),
    completedAt: task.completedAt,
    completedByName: null,
    notes: task.notes,
    waitingOnAssets: task.waitingOnAssets ?? [],
    waitingOnLoans: (task.waitingOnLoans ?? []).map((loan) => ({
      ...loan,
      balance: moneyFromDecimalStringOrZero(loan.balance),
    })),
    ...(task.accessRevocation ? { accessRevocation: task.accessRevocation } : {}),
  };
}

interface ApiOnboardingCase {
  id: string;
  employeeId: string;
  employee: {
    id: string;
    fullName: string;
    employeeNumber: string;
    department: { id: string; name: string } | null;
    position: { id: string; title: string } | null;
    manager: { id: string; fullName: string } | null;
  } | null;
  startDate: string;
  status: string;
  tasks: ApiWorkflowTask[];
  createdAt: string;
  closedAt: string | null;
}

function toOnboardingCase(row: ApiOnboardingCase): OnboardingCase {
  return {
    id: row.id,
    employeeId: row.employeeId,
    employeeName: row.employee?.fullName ?? '',
    positionTitle: row.employee?.position?.title ?? '',
    departmentName: row.employee?.department?.name ?? '',
    managerName: row.employee?.manager?.fullName ?? null,
    startDate: row.startDate.slice(0, 10),
    // Templates are not modelled on the API; the default checklist is created
    // in code. Null is accurate, an invented id would not resolve.
    templateId: null,
    status: narrow(TASK_STATUSES, row.status, 'case status'),
    tasks: row.tasks.map(toWorkflowTask),
    createdAt: row.createdAt,
    closedAt: row.closedAt,
  };
}

export const apiOnboardingService = {
  async list(
    filter: { status?: string; page?: number; pageSize?: number } = {},
  ): Promise<Page<OnboardingCase>> {
    const rows = await apiTransport().get<ApiOnboardingCase[]>('/api/v1/onboarding-cases');
    const mapped = rows
      .map(toOnboardingCase)
      .filter((row) => !filter.status || filter.status === 'all' || row.status === filter.status);

    // This endpoint returns an array rather than a page — it is capped at 200
    // server-side, which is the real bound. Reporting the mapped length as the
    // total is honest for that shape.
    return {
      rows: mapped,
      total: mapped.length,
      page: filter.page ?? 1,
      pageSize: filter.pageSize ?? mapped.length,
    };
  },

  /**
   * Complete one checklist task.
   *
   * The actor is accepted to keep the prototype's signature and never sent —
   * the server records `completedByUserId` from the session. The task route
   * answers with the task; the screen renders the case, so the case is
   * re-read from the same list the table uses.
   *
   * The prototype closes a case when its last task completes. The server keeps
   * closing a separate, guarded act — a case cannot complete with blocking
   * work open — so the same behaviour goes through the guarded route rather
   * than being asserted client-side.
   */
  async completeTask(caseId: string, taskId: string, _actor: AuditActor): Promise<OnboardingCase> {
    await apiTransport().post(`/api/v1/workflow-tasks/${taskId}/complete`, {});

    const rows = await apiTransport().get<ApiOnboardingCase[]>('/api/v1/onboarding-cases');
    const row = rows.find((candidate) => candidate.id === caseId);
    const current = found(row ? toOnboardingCase(row) : null, 'onboarding case');

    const allDone = current.tasks.every(
      (task) => task.status === 'completed' || task.status === 'cancelled',
    );
    if (allDone && current.status !== 'completed') {
      const closed = await apiTransport().post<{ status: string; closedAt: string | null }>(
        `/api/v1/onboarding-cases/${caseId}/complete`,
        {},
      );
      return {
        ...current,
        status: narrow(TASK_STATUSES, closed.status, 'case status'),
        closedAt: closed.closedAt,
      };
    }

    return current;
  },
};

interface ApiCaseEmployee {
  id: string;
  fullName: string;
  employeeNumber: string;
  dateHired?: string;
  department: { id: string; name: string } | null;
  position: { id: string; title: string } | null;
  manager: { id: string; fullName: string } | null;
}

interface ApiProbationReview {
  id: string;
  employeeId: string;
  employee: ApiCaseEmployee | null;
  probationEndDate: string;
  reviewDueDate: string;
  managerRecommendation: string;
  managerComment: string | null;
  hrDecision: string;
  hrComment: string | null;
  decidedAt: string | null;
  decidedByUserId: string | null;
  decidedByUser?: { displayName: string } | null;
  /** Whether the employee is on probation today, by the server's one definition. */
  employeeOnProbation?: boolean;
  /** A probationary hire whose start date is still ahead. */
  startsLater?: boolean;
}

function toProbationReview(row: ApiProbationReview): ProbationReview {
  return {
    id: row.id,
    employeeId: row.employeeId,
    employeeName: row.employee?.fullName ?? '',
    positionTitle: row.employee?.position?.title ?? '',
    departmentName: row.employee?.department?.name ?? '',
    managerName: row.employee?.manager?.fullName ?? null,
    startDate: (row.employee?.dateHired ?? '').slice(0, 10),
    probationEndDate: row.probationEndDate.slice(0, 10),
    reviewDueDate: row.reviewDueDate.slice(0, 10),
    /*
     * Zeroes, not a guess.
     *
     * The attendance summary is a separate aggregation the review endpoint does
     * not run. Zero present days is visibly wrong and prompts somebody to open
     * the attendance tab; a plausible invented figure is worse, because a
     * probation decision might be made on it.
     */
    attendanceSummary: { presentDays: 0, lateCount: 0, absentCount: 0 },
    managerRecommendation: narrow(
      PROBATION_DECISIONS,
      row.managerRecommendation,
      'probation recommendation',
    ),
    managerComment: row.managerComment,
    hrDecision: narrow(PROBATION_DECISIONS, row.hrDecision, 'probation decision'),
    hrComment: row.hrComment,
    decidedAt: row.decidedAt,
    decidedByUserId: row.decidedByUserId ?? null,
    decidedByName: row.decidedByUser?.displayName ?? null,
    startsLater: row.startsLater ?? false,
  };
}

/** The HR decisions that count as decided: the Decision filter's outcomes. */
const DECIDED_OUTCOMES = new Set(['confirm_employment', 'extend', 'end_employment']);

export const apiProbationService = {
  async list(filter: PageRequest & { decision?: string } = {}): Promise<Page<ProbationReview>> {
    const rows = await apiTransport().get<ApiProbationReview[]>('/api/v1/probation-reviews');
    // The page sends `decision`; this used to read `status`, so the Decision
    // filter and the search box changed nothing.
    const mapped = rows
      .map(toProbationReview)
      .filter(
        (row) =>
          !filter.decision || filter.decision === 'all' || row.hrDecision === filter.decision,
      );

    return paginate(
      mapped as unknown as Record<string, unknown>[],
      filter,
      (row) =>
        `${String(row.employeeName)} ${String(row.positionTitle)} ${String(row.departmentName)}`,
    ) as unknown as Page<ProbationReview>;
  },

  /**
   * The rail's numbers, counted from the same list the table renders.
   *
   * There is no probation aggregation route; deriving the counts from
   * `/probation-reviews` keeps the rail and the table incapable of
   * disagreeing. Overdue is measured against the review due date — the review
   * has to happen before the probation ends.
   */
  async summary() {
    const rows = await apiTransport().get<ApiProbationReview[]>('/api/v1/probation-reviews');
    /*
     * A hire who has not started is listed, but awaits no decision yet. An
     * extended probation does: it is decided again when its new review date
     * comes round.
     */
    const open = (row: ApiProbationReview) =>
      row.hrDecision === 'pending' || row.hrDecision === 'extend';
    const pending = rows.filter((row) => open(row) && !row.startsLater);
    const startingLater = rows.filter((row) => row.hrDecision === 'pending' && row.startsLater);
    const today = todayInBusiness();
    const inFortnight = addDaysToKey(today, 14);

    const dueDate = (row: ApiProbationReview) => row.reviewDueDate.slice(0, 10);

    return {
      /*
       * People, not reviews, and by the server's definition — the same one
       * the overview's and the workforce report's "On probation" count, so
       * the three figures are one figure.
       */
      onProbation: new Set(
        rows.filter((row) => row.employeeOnProbation).map((row) => row.employeeId),
      ).size,
      /*
       * Awaiting a decision is the decision window — overdue plus due within
       * the fortnight — not every undecided review. Somebody hired today, their
       * review due next March, awaits nothing yet; counting them made this
       * figure larger than overdue and due-soon together.
       */
      pending: pending.filter((row) => dueDate(row) <= inFortnight).length,
      overdue: pending.filter((row) => dueDate(row) < today).length,
      dueSoon: pending.filter((row) => dueDate(row) >= today && dueDate(row) <= inFortnight).length,
      startingLater: startingLater.length,
      /*
       * Every review HR has decided — confirmed, extended or not confirmed —
       * the three outcomes the Decision filter lists after "Pending". Leaving
       * extensions out showed 0 beside Enrique Cruz Torres's "Extended".
       */
      decided: rows.filter((row) => DECIDED_OUTCOMES.has(row.hrDecision)).length,
    };
  },

  /**
   * Record the HR decision.
   *
   * The server refuses a second decision and, on confirmation, performs the
   * employment-status transition in the same transaction — none of that is
   * duplicated here. The decide route answers with the bare review row, so the
   * decided review is re-read from the list the screen renders.
   */
  async decide(
    id: string,
    decision: ProbationReview['hrDecision'],
    comment: string,
    _actor: AuditActor,
    options: { probationEndDate?: string } = {},
  ): Promise<ProbationReview> {
    // An extension carries its new end date; the server moves the review with it.
    await apiTransport().post(`/api/v1/probation-reviews/${id}/decide`, {
      decision,
      comment,
      ...(decision === 'extend' && options.probationEndDate
        ? { probationEndDate: options.probationEndDate }
        : {}),
    });

    const rows = await apiTransport().get<ApiProbationReview[]>('/api/v1/probation-reviews');
    const row = rows.find((candidate) => candidate.id === id);
    return found(row ? toProbationReview(row) : null, 'probation review');
  },
};

interface ApiOffboardingCase {
  id: string;
  referenceCode: string;
  employeeId: string;
  employee: ApiCaseEmployee | null;
  reason: string;
  notes?: string | null;
  noticeDate: string;
  lastWorkingDay: string;
  status: string;
  assetsOutstanding: number;
  accessRevoked: boolean;
  exitInterviewCompleted: boolean;
  finalPayStatus: string;
  createdAt: string;
  tasks: ApiWorkflowTask[];
  closedAt: string | null;
  /** From the loan register: what active and approved loans still owe. Null once closed. */
  loanBalance?: string | null;
  outstandingLoans?: { referenceCode: string; loanType: string; balance: string }[];
  /** From the leave ledger, as it stands today. */
  leaveBalanceDays?: string | null;
  leaveBalances?: { code: string; name: string; available: string }[];
  openRequiredTasks?: string[];
}

function toOffboardingCase(row: ApiOffboardingCase): OffboardingCase {
  return {
    id: row.id,
    referenceCode: row.referenceCode,
    employeeId: row.employeeId,
    employeeName: row.employee?.fullName ?? '',
    positionTitle: row.employee?.position?.title ?? '',
    departmentName: row.employee?.department?.name ?? '',
    managerName: row.employee?.manager?.fullName ?? null,
    // Descriptive, so one unrecognised reason shows in words instead of
    // failing the whole list for everybody.
    reason: narrowDescriptive(OFFBOARDING_REASONS, row.reason),
    reasonLabel: offboardingReasonLabel(row.reason),
    notes: row.notes ?? null,
    noticeDate: row.noticeDate.slice(0, 10),
    lastWorkingDay: row.lastWorkingDay.slice(0, 10),
    status: narrow(TASK_STATUSES, row.status, 'offboarding case status'),
    clearanceTasks: row.tasks.map(toWorkflowTask),
    // The server counts assets still assigned and refuses to close the case
    // while any remain, so this figure is the one the refusal is based on.
    assetsOutstanding: row.assetsOutstanding,
    /*
     * From the loan register, summed by the server over the leaver's active
     * and approved loans. Null only when the server did not send it (a closed
     * case), never zero for "unknown".
     */
    loanBalance:
      row.loanBalance === undefined || row.loanBalance === null
        ? null
        : moneyFromDecimalStringOrZero(row.loanBalance),
    outstandingLoans: (row.outstandingLoans ?? []).map((loan) => ({
      ...loan,
      balance: moneyFromDecimalStringOrZero(loan.balance),
    })),
    // The server calls this `accessRevoked`; same fact, older name here.
    accountDisabled: row.accessRevoked,
    // Leave available today, from the ledger; the exit interview notes are a
    // separate document.
    leaveBalanceDays:
      row.leaveBalanceDays === undefined || row.leaveBalanceDays === null
        ? null
        : Number(row.leaveBalanceDays),
    leaveBalances: (row.leaveBalances ?? []).map((balance) => ({
      ...balance,
      available: Number(balance.available),
    })),
    openRequiredTasks: row.openRequiredTasks,
    exitInterviewCompleted: row.exitInterviewCompleted,
    exitInterviewNotes: null,
    finalPayStatus: narrowFinalPayStatus(row.finalPayStatus),
    createdAt: row.createdAt,
    closedAt: row.closedAt,
  };
}

/** Readiness only — final pay is a payroll computation, not a status here. */
function narrowFinalPayStatus(value: string): OffboardingCase['finalPayStatus'] {
  return narrow(
    ['not_started', 'ready_for_payroll', 'processed'] as const,
    value,
    'final pay status',
  );
}

/** One offboarding case, re-read after a write with its tasks and employee joined. */
async function refetchOffboardingCase(caseId: string): Promise<OffboardingCase> {
  const rows = await apiTransport().get<ApiOffboardingCase[]>('/api/v1/offboarding-cases');
  const row = rows.find((candidate) => candidate.id === caseId);
  return found(row ? toOffboardingCase(row) : null, 'offboarding case');
}

export const apiOffboardingService = {
  async list(filter: PageRequest & { status?: string } = {}): Promise<Page<OffboardingCase>> {
    const rows = await apiTransport().get<ApiOffboardingCase[]>('/api/v1/offboarding-cases');
    const mapped = rows
      .map(toOffboardingCase)
      .filter((row) => !filter.status || filter.status === 'all' || row.status === filter.status);

    // Search by employee or reference, as the search box says.
    return paginate(
      mapped as unknown as Record<string, unknown>[],
      filter,
      (row) =>
        `${String(row.employeeName)} ${String(row.referenceCode)} ${String(row.departmentName)}`,
    ) as unknown as Page<OffboardingCase>;
  },

  /**
   * Complete one clearance task. Clearance tasks are workflow tasks — the same
   * table onboarding uses — so the same route completes them; the actor is the
   * session's, not the argument's.
   */
  async completeClearanceTask(
    caseId: string,
    taskId: string,
    _actor: AuditActor,
  ): Promise<OffboardingCase> {
    await apiTransport().post(`/api/v1/workflow-tasks/${taskId}/complete`, {});
    return refetchOffboardingCase(caseId);
  },

  /**
   * Close the case.
   *
   * The server's guards are the ones the specification is most explicit
   * about — no closing with clearance outstanding, no closing while company
   * assets remain assigned — and it performs the employment-status transition
   * in the same transaction. The leaver's login is disabled by the "Revoke
   * system access" clearance task — at once when it is completed on or after
   * the last working day, otherwise by the server on that day.
   */
  async close(caseId: string, _actor: AuditActor): Promise<OffboardingCase> {
    await apiTransport().post(`/api/v1/offboarding-cases/${caseId}/complete`, {});
    return refetchOffboardingCase(caseId);
  },

  /**
   * Who a separation can be started for, by the server's rule: the current
   * workforce, less anybody already in an open case.
   */
  async eligibleEmployees(): Promise<OffboardingCandidate[]> {
    const rows = await apiTransport().get<
      {
        id: string;
        fullName: string;
        employeeNumber: string;
        department: { name: string } | null;
        position: { title: string } | null;
      }[]
    >('/api/v1/offboarding-cases/eligible-employees');
    return rows.map((row) => ({
      id: row.id,
      fullName: row.fullName,
      employeeNumber: row.employeeNumber,
      departmentName: row.department?.name ?? '',
      positionTitle: row.position?.title ?? '',
    }));
  },

  /**
   * Start a separation. The server opens the case with the standard clearance
   * checklist and refuses a second open case for the same person.
   */
  async open(input: OffboardingInput, _actor: AuditActor): Promise<OffboardingCase> {
    const created = await apiTransport().post<{ id: string }>('/api/v1/offboarding-cases', {
      employeeId: input.employeeId,
      reason: input.reason,
      noticeDate: input.noticeDate,
      lastWorkingDay: input.lastWorkingDay,
      ...(input.notes?.trim() ? { notes: input.notes.trim() } : {}),
    });
    return refetchOffboardingCase(created.id);
  },
};

/** Somebody a separation can be started for. */
export interface OffboardingCandidate {
  id: string;
  fullName: string;
  employeeNumber: string;
  departmentName: string;
  positionTitle: string;
}

/** What starting a separation takes. */
export interface OffboardingInput {
  employeeId: string;
  reason: OffboardingReason;
  noticeDate: string;
  lastWorkingDay: string;
  notes?: string;
}

interface ApiPerformanceReview {
  id: string;
  cycleId: string;
  cycle: { name: string; code: string } | null;
  employeeId: string;
  employee: {
    id: string;
    fullName: string;
    position: { id: string; title: string } | null;
    manager: { id: string; fullName: string } | null;
  } | null;
  status: string;
  selfRating: number | null;
  managerRating: number | null;
  /** Decimal string once finalised — Prisma serialises Decimal(4,2) as "4.00". */
  finalRating: string | null;
  selfComment: string | null;
  managerComment: string | null;
  /** The manager's review comment. Finalisation no longer overwrites it. */
  summary: string | null;
  /** HR's note from the finalize route. */
  finalizationNote?: string | null;
  acknowledgedAt: string | null;
  updatedAt: string;
}

function toPerformanceReview(row: ApiPerformanceReview): PerformanceReview {
  return {
    id: row.id,
    cycleId: row.cycleId,
    cycleName: row.cycle?.name ?? '',
    employeeId: row.employeeId,
    employeeName: row.employee?.fullName ?? '',
    positionTitle: row.employee?.position?.title ?? '',
    managerName: row.employee?.manager?.fullName ?? null,
    status: narrow(REVIEW_STATUSES, row.status, 'review status'),
    // Goals and competency ratings are separate records the list does not
    // carry. Empty arrays render as "none recorded", which is what the list
    // actually knows; the review detail loads them.
    goals: [],
    competencyRatings: [],
    selfReviewComment: row.selfComment,
    // The manager's comment, as the manager wrote it. HR's finalisation note
    // is its own field: finalising used to replace this one.
    managerComment: row.summary ?? row.managerComment,
    finalizationNote: row.finalizationNote ?? null,
    // The final rating only once there is one. Before finalisation the
    // manager's rating travels as itself: shown as "Final rating", a rating
    // nobody had settled yet read as the result. Never an average with the
    // self-assessment — that would present the employee's own view as part of
    // their result.
    finalRating: row.finalRating !== null ? Number(row.finalRating) : null,
    managerRating: row.managerRating,
    selfRating: row.selfRating,
    developmentPlan: null,
    acknowledgedAt: row.acknowledgedAt,
    updatedAt: row.updatedAt,
  };
}

interface ApiPerformanceCycle {
  id: string;
  code: string;
  name: string;
  startDate: string;
  endDate: string;
  status: string;
  reviewCount: number;
  completedCount: number;
}

export const apiPerformanceService = {
  /**
   * Performance cycles.
   *
   * They existed server-side only as a relation hanging off a review, so this
   * list fell to the mock: the screens could show which cycle a review
   * belonged to but not list the cycles or say how far through one the
   * organisation was.
   *
   * `selfReviewDue` and `managerReviewDue` have no server column. They fall
   * back to the cycle end rather than inventing an intermediate deadline the
   * server would not enforce.
   */
  async cycles(): Promise<ReviewCycle[]> {
    const rows = await apiTransport().get<ApiPerformanceCycle[]>('/api/v1/performance-cycles');

    return rows.map((row) => ({
      id: row.id,
      name: row.name,
      periodStart: row.startDate as ReviewCycle['periodStart'],
      periodEnd: row.endDate as ReviewCycle['periodEnd'],
      status: row.status as ReviewCycle['status'],
      selfReviewDue: row.endDate as ReviewCycle['selfReviewDue'],
      managerReviewDue: row.endDate as ReviewCycle['managerReviewDue'],
      eligibleEmployeeCount: row.reviewCount,
      completedCount: row.completedCount,
    }));
  },

  /**
   * Named `reviews`, exactly as the prototype names it.
   *
   * This method was `list`, which the `{...mock, ...api}` spread never
   * overrode — the screens call `reviews`, so the review register silently
   * read the prototype's empty store in API mode. The spread only replaces
   * identical names.
   */
  async reviews(
    filter: { cycleId?: string; status?: string; page?: number; pageSize?: number } = {},
  ): Promise<Page<PerformanceReview>> {
    const rows = await apiTransport().get<ApiPerformanceReview[]>('/api/v1/performance-reviews');
    const mapped = rows
      .map(toPerformanceReview)
      .filter((row) => !filter.cycleId || row.cycleId === filter.cycleId)
      .filter((row) => !filter.status || filter.status === 'all' || row.status === filter.status);

    return {
      rows: mapped,
      total: mapped.length,
      page: filter.page ?? 1,
      pageSize: filter.pageSize ?? mapped.length,
    };
  },

  /**
   * Finalise a review.
   *
   * One-way on the server — a finalised rating cannot quietly become a
   * different one — and that refusal surfaces as the server's own message.
   * The route answers with the bare row, so the finalised review is re-read
   * from the list with its cycle and employee joined.
   */
  async finalize(
    id: string,
    rating: number,
    comment: string,
    _actor: AuditActor,
  ): Promise<PerformanceReview> {
    await apiTransport().post(`/api/v1/performance-reviews/${id}/finalize`, {
      finalRating: rating,
      note: comment,
    });

    const page = await apiPerformanceService.reviews();
    return found(
      page.rows.find((row) => row.id === id),
      'performance review',
    );
  },
};

interface ApiEmployeeDocument {
  id: string;
  employeeId: string;
  employee: { id: string; fullName: string } | null;
  documentType: string;
  name: string;
  issuedOn: string | null;
  expiresOn: string | null;
  verifiedAt: string | null;
  verifiedByUserId: string | null;
  /** Resolved by the list; absent from the verify route's bare row. */
  verifiedByName?: string | null;
  createdAt: string;
}

function toDocument(row: ApiEmployeeDocument): EmployeeDocumentRecord {
  return {
    id: row.id,
    employeeId: row.employeeId,
    employeeName: row.employee?.fullName ?? '',
    typeId: row.documentType,
    name: row.name,
    // The stored object's own filename is not on the list row; showing the
    // document's name beats showing an object key, which is the alternative.
    fileName: row.name,
    status: documentStatus(row),
    issuedOn: row.issuedOn ? row.issuedOn.slice(0, 10) : null,
    expiresOn: row.expiresOn ? row.expiresOn.slice(0, 10) : null,
    // The list resolves the verifier's account to a name. Null rather than a
    // placeholder when it cannot, so "verified by" is either a person or
    // visibly absent.
    verifiedByName: row.verifiedByName ?? null,
    verifiedAt: row.verifiedAt,
    uploadedAt: row.createdAt,
  };
}

export const apiDocumentAdminService = {
  async list(
    filter: {
      status?: string;
      employeeId?: string;
      search?: string;
      page?: number;
      pageSize?: number;
    } = {},
  ): Promise<Page<EmployeeDocumentRecord>> {
    const pageSize = filter.pageSize ?? 50;
    const pageNumber = filter.page ?? 1;

    // `employeeId` is the only filter the route's schema parses; a `limit`
    // would be stripped, so it is not sent. The route caps itself at 500.
    const page = await apiTransport().page<ApiEmployeeDocument>('/api/v1/documents', {
      query: { employeeId: filter.employeeId },
    });

    const rows = page.items.map(toDocument);

    // Status is derived here, so filtering on it is too: the stored column
    // does not encode the "expiring" window the screens use.
    const needle = filter.search?.trim().toLowerCase();
    const filtered = rows
      .filter((row) => !filter.status || filter.status === 'all' || row.status === filter.status)
      .filter(
        (row) =>
          !needle || `${row.employeeName} ${row.name} ${row.typeId}`.toLowerCase().includes(needle),
      );

    /*
     * The total and the page are the filtered set's. The footer used to
     * report the unfiltered total ("1–25 of 170, 1/7") beside the four rows a
     * filter actually matched, and every page past the first was empty.
     */
    const start = (pageNumber - 1) * pageSize;
    return {
      rows: filtered.slice(start, start + pageSize),
      total: filtered.length,
      page: pageNumber,
      pageSize,
    };
  },

  /**
   * One document, for a link that landed outside the loaded page. The API has
   * no GET /documents/:id, so the record is found in the same list the table
   * renders rather than requested from a route that answers 404 for every id.
   */
  async get(id: string): Promise<EmployeeDocumentRecord | null> {
    const page = await apiTransport().page<ApiEmployeeDocument>('/api/v1/documents');
    const row = page.items.find((candidate) => candidate.id === id);
    return row ? toDocument(row) : null;
  },

  /**
   * Verify a document.
   *
   * A claim somebody stands behind: the server stamps the verifier from the
   * session and audits it, so the actor argument is the prototype's shape and
   * nothing more. The verify route answers without the employee join, so the
   * verified record is re-read from the list.
   */
  async verify(id: string, _actor: AuditActor): Promise<EmployeeDocumentRecord> {
    await apiTransport().post(`/api/v1/documents/${id}/verify`, {});
    return found(await apiDocumentAdminService.get(id), 'document');
  },
};

interface ApiPolicy {
  id: string;
  code: string;
  title: string;
  category: string | null;
  summary: string | null;
  body: string;
  version: number;
  status: string;
  requiresAcknowledgement: boolean;
  effectiveFrom: string | null;
  publishedAt: string | null;
}

function toPolicy(row: ApiPolicy): PolicyRecord {
  return {
    id: row.id,
    title: row.title,
    category: row.category ?? '',
    // The API versions numerically; the screen shows a label. Rendering the
    // bare number would read as a count rather than a version.
    version: `v${row.version}`,
    // The effective date, not the publication date. A policy published on
    // Friday to take effect on Monday is not in force on Friday, and the column
    // is headed "Effective".
    effectiveDate: row.effectiveFrom
      ? row.effectiveFrom.slice(0, 10)
      : row.publishedAt
        ? businessDateOf(row.publishedAt)
        : '',
    status: row.status as PolicyRecord['status'],
    requiresAcknowledgement: row.requiresAcknowledgement,
    // Targeting is not modelled on the API yet; every published policy reaches
    // the organisation. Saying "All employees" is accurate today and will need
    // revisiting when audiences arrive.
    audience: 'All employees',
    summary: row.summary ?? '',
  };
}

export const apiPolicyService = {
  /**
   * Acknowledgement progress, from the server's own count.
   *
   * Acknowledgements were writable and unreadable — `POST /policies/:id/
   * acknowledge` recorded them and no route read them back — so this screen
   * fell to the mock and showed prototype numbers next to real policies. The
   * route now computes the outstanding list from active employees rather than
   * storing it, so somebody who joins after publication appears without a
   * backfill.
   */
  async acknowledgementProgress(id: string) {
    const response = await apiTransport().get<{
      summary: { required: number; acknowledged: number; outstanding: number };
    }>(`/api/v1/policies/${id}/acknowledgements`);

    return response.summary;
  },

  async list(
    filter: { status?: string; page?: number; pageSize?: number } = {},
  ): Promise<Page<PolicyRecord>> {
    // The policies route parses no query at all — a `status` or `limit` sent
    // here silently never filtered anything — so the status filter happens on
    // the rows the server actually chose to send (drafts only for a caller
    // who can publish).
    const rows = (await apiTransport().get<ApiPolicy[]>('/api/v1/policies'))
      .map(toPolicy)
      .filter((row) => !filter.status || filter.status === 'all' || row.status === filter.status);

    return {
      rows,
      total: rows.length,
      page: filter.page ?? 1,
      pageSize: filter.pageSize ?? rows.length,
    };
  },

  /**
   * Publish a policy.
   *
   * The server refuses a re-publish, stamps the publisher from the session,
   * audits it, and broadcasts the one event that legitimately reaches the
   * whole organisation. The route answers with the updated policy row.
   */
  async publish(id: string, _actor: AuditActor): Promise<PolicyRecord> {
    return toPolicy(await apiTransport().post<ApiPolicy>(`/api/v1/policies/${id}/publish`, {}));
  },
};

/*
 * The server's announcement, as stored. It has no category and no
 * acknowledgement flag — this interface once declared both, so the Category
 * column was blank on every row — and it does have a priority and a targeted
 * audience ("all", "department:ENG", "work_location:<id>"), which the screen
 * then ignored in favour of "All employees" for everything.
 */
interface ApiAnnouncement {
  id: string;
  title: string;
  body: string;
  status: string;
  priority: string | null;
  audience: string | null;
  publishedAt: string | null;
  expiresAt: string | null;
}

function toAnnouncement(row: ApiAnnouncement): AnnouncementRecord {
  return {
    id: row.id,
    title: row.title,
    category: '',
    priority: row.priority ?? 'normal',
    status: row.status as AnnouncementRecord['status'],
    publishedAt: row.publishedAt,
    expiresOn: row.expiresAt ? businessDateOf(row.expiresAt) : null,
    // The raw target; the screen names the department or location in it.
    audience: row.audience ?? 'all',
    requiresAcknowledgement: false,
    body: row.body,
  };
}

export const apiAnnouncementService = {
  async list(
    filter: { status?: string; search?: string; page?: number; pageSize?: number } = {},
  ): Promise<Page<AnnouncementRecord>> {
    // Same as policies: the route parses no query, so the sent filters were
    // silently ignored. The route caps itself at 100; filtering happens here.
    // The status is the server's, which already reads a published
    // announcement past its expiry as `expired`.
    const search = filter.search?.trim().toLowerCase() ?? '';
    const rows = (await apiTransport().get<ApiAnnouncement[]>('/api/v1/announcements'))
      .map(toAnnouncement)
      .filter((row) => !filter.status || filter.status === 'all' || row.status === filter.status)
      .filter((row) => !search || row.title.toLowerCase().includes(search));

    return {
      rows,
      total: rows.length,
      page: filter.page ?? 1,
      pageSize: filter.pageSize ?? rows.length,
    };
  },

  /** Publish an announcement. The broadcast to every session is the server's. */
  async publish(id: string, _actor: AuditActor): Promise<AnnouncementRecord> {
    return toAnnouncement(
      await apiTransport().post<ApiAnnouncement>(`/api/v1/announcements/${id}/publish`, {}),
    );
  },
};

interface ApiAsset {
  id: string;
  assetCode: string;
  name: string;
  category: string;
  serialNumber: string | null;
  condition: string;
  status: string;
  assignedEmployeeId: string | null;
  assignedEmployeeName: string | null;
  isActive: boolean;
  createdAt: string;
  /**
   * The custody trail, oldest first. On the list only — the assign, return
   * and status routes answer with the bare asset row.
   */
  assignments?: ApiAssetAssignment[];
  statusEvents?: {
    id: string;
    fromStatus: string;
    toStatus: string;
    reason: string;
    actorName: string | null;
    createdAt: string;
  }[];
}

interface ApiAssetAssignment {
  id: string;
  employeeId: string;
  employeeName: string | null;
  assignedAt: string;
  assignedByName?: string | null;
  returnedAt: string | null;
  returnedByName?: string | null;
  returnCondition: string | null;
  returnNotes: string | null;
}

function toAssignmentEntry(entry: ApiAssetAssignment): Asset['history'][number] {
  return {
    id: entry.id,
    employeeId: entry.employeeId,
    employeeName: entry.employeeName,
    // Timestamps, dated on the organisation's calendar: an asset handed over at
    // 00:30 in Manila was assigned that day, not the UTC day before.
    assignedOn: businessDateOf(entry.assignedAt),
    returnedOn: entry.returnedAt ? businessDateOf(entry.returnedAt) : null,
    assignedAt: entry.assignedAt,
    returnedAt: entry.returnedAt,
    assignedByName: entry.assignedByName ?? null,
    returnedByName: entry.returnedByName ?? null,
    // Returns speak good/fair/poor/damaged; `damaged` is not an asset
    // *condition* in the shared vocabulary but it is a real return outcome,
    // so it is carried through for the history line rather than dropped.
    conditionOnReturn:
      (ASSET_CONDITIONS as readonly string[]).includes(entry.returnCondition ?? '') ||
      entry.returnCondition === 'damaged'
        ? (entry.returnCondition as Asset['condition'])
        : null,
    notes: entry.returnNotes,
  };
}

/**
 * An asset, mapped.
 *
 * The custody trail comes from the server's assignment records, never from
 * the current holder: inventing an entry from `assignedEmployeeName` would
 * claim they have had it since the record was created, which is exactly the
 * question the trail exists to answer correctly. A response without the trail
 * (a write's bare row) maps to an empty one, and the list re-read after every
 * write fills it.
 */
function toAsset(row: ApiAsset): Asset {
  const history = (row.assignments ?? []).map(toAssignmentEntry);
  const open = [...history].reverse().find((entry) => entry.returnedOn === null);

  return {
    id: row.id,
    assetCode: row.assetCode,
    name: row.name,
    category: row.category,
    serialNumber: row.serialNumber ?? '',
    condition: narrow(ASSET_CONDITIONS, row.condition, 'asset condition'),
    status: narrow(ASSET_STATUSES, row.status, 'asset status'),
    workLocationId: null,
    workLocationName: null,
    assignedEmployeeId: row.assignedEmployeeId,
    assignedEmployeeName: row.assignedEmployeeName,
    // From the open assignment, when the current holder has one. Null rather
    // than today's date, which would be a fabricated custody date.
    assignedOn: row.assignedEmployeeId && open ? open.assignedOn : null,
    purchaseCost: null,
    notes: null,
    history,
    statusEvents: (row.statusEvents ?? []).map((event) => ({
      id: event.id,
      fromStatus: narrow(ASSET_STATUSES, event.fromStatus, 'asset status'),
      toStatus: narrow(ASSET_STATUSES, event.toStatus, 'asset status'),
      reason: event.reason,
      actorName: event.actorName,
      at: event.createdAt,
    })),
    createdAt: row.createdAt,
  };
}

export const apiAssetService = {
  /**
   * A status change that is not a custody change.
   *
   * The server owns the rule this used to skip: an asset somebody still holds
   * cannot be marked available or under repair, only lost. The mock happily
   * wrote either, which is how a status saying `available` ends up beside an
   * assignment row saying otherwise.
   *
   * The audit actor the prototype signature carries is not sent — the server
   * resolves it from the session.
   */
  async setStatus(id: string, status: Asset['status'], reason: string): Promise<Asset> {
    return toAsset(
      await apiTransport().post<ApiAsset>(`/api/v1/assets/${id}/status`, { status, reason }),
    );
  },

  /**
   * Counted from the same list the table renders — the rail was still the
   * prototype's 96 assets above a real inventory of one.
   */
  async summary() {
    const rows = await apiTransport().get<{ status: string }[]>('/api/v1/assets');
    return {
      total: rows.length,
      assigned: rows.filter((row) => row.status === 'assigned').length,
      available: rows.filter((row) => row.status === 'available').length,
      maintenance: rows.filter((row) => row.status === 'maintenance').length,
      lostOrDamaged: rows.filter((row) => row.status === 'lost' || row.status === 'damaged').length,
      // Every status has a count, so the tiles add up to the total.
      retired: rows.filter((row) => row.status === 'retired').length,
    };
  },

  async list(
    filter: { status?: string; category?: string; page?: number; pageSize?: number } = {},
  ): Promise<Page<Asset>> {
    // The assets route parses no query — the status and category filters this
    // adapter used to send were silently ignored, so the dropdowns did
    // nothing. The route caps itself at 500; filtering happens here.
    const rows = (await apiTransport().get<ApiAsset[]>('/api/v1/assets'))
      .map(toAsset)
      .filter((row) => !filter.status || filter.status === 'all' || row.status === filter.status)
      .filter(
        (row) => !filter.category || filter.category === 'all' || row.category === filter.category,
      );

    return {
      rows,
      total: rows.length,
      page: filter.page ?? 1,
      pageSize: filter.pageSize ?? rows.length,
    };
  },

  async get(id: string): Promise<Asset | null> {
    // The API has no GET /assets/:id — the assets module is list-only — so the
    // detail is found in the list, the same list the table renders, rather than
    // requested from a route that answers 404 for every asset.
    const rows = await apiTransport().get<ApiAsset[]>('/api/v1/assets');
    const row = rows.find((candidate) => candidate.id === id);
    return row ? toAsset(row) : null;
  },

  /**
   * Assign an asset.
   *
   * The employee name is accepted to match the prototype's signature and never
   * sent — the server resolves the holder's name itself, and appends to the
   * custody trail inside the same transaction. Double-assignment is the
   * server's refusal, surfaced verbatim.
   */
  async assign(
    id: string,
    employeeId: string,
    _employeeName: string,
    _actor: AuditActor,
  ): Promise<Asset> {
    return toAsset(
      await apiTransport().post<ApiAsset>(`/api/v1/assets/${id}/assign`, { employeeId }),
    );
  },

  /**
   * Record a return.
   *
   * The server's return vocabulary is good/fair/poor/damaged — an asset coming
   * back is no longer "new", and that condition is refused with the server's
   * own message rather than translated here. Damaged routes the asset to the
   * damaged pool server-side.
   */
  async recordReturn(
    id: string,
    condition: Asset['condition'],
    notes: string,
    _actor: AuditActor,
  ): Promise<Asset> {
    return toAsset(
      await apiTransport().post<ApiAsset>(`/api/v1/assets/${id}/return`, {
        condition,
        ...(notes.trim() ? { notes } : {}),
      }),
    );
  },
};

interface ApiCourse {
  id: string;
  code: string;
  title: string;
  category: string | null;
  isMandatory: boolean;
  validityMonths: number | null;
  isActive: boolean;
}

function toCourse(row: ApiCourse): Course {
  return {
    id: row.id,
    code: row.code,
    title: row.title,
    // Neither a description nor a duration is modelled on the API. Empty and
    // zero rather than invented copy — a course card claiming "8 hours" for a
    // course whose length nobody recorded would be read as a fact.
    description: '',
    category: row.category ?? '',
    mandatory: row.isMandatory,
    durationHours: 0,
    validityMonths: row.validityMonths,
    active: row.isActive,
  };
}

/**
 * Courses, not training records.
 *
 * The learning screen shows both: a catalogue of courses, and each employee's
 * completion. `/courses` is the catalogue; enrolments are a separate record.
 * Mapping a course into a `TrainingRecord` with an empty employee would produce
 * rows that look like somebody's training history and are not — so only the
 * catalogue read is covered here, and the mock's enrolment surface is untouched
 * until a training-records list endpoint exists.
 *
 * `/courses` answers the plain `{data}` envelope and parses no query, so
 * nothing is sent that the server would strip; search filters here.
 */
const TRAINING_STATUSES = ['enrolled', 'attended', 'completed', 'no_show', 'expired'] as const;

interface ApiTrainingRecord {
  id: string;
  courseId: string;
  employeeId: string;
  status: string;
  completedOn: string | null;
  expiresOn: string | null;
  score: number | null;
}

interface ApiTrainingRecord {
  id: string;
  employeeId: string;
  employeeName: string | null;
  courseId: string;
  status: string;
  completedOn: string | null;
  expiresOn: string | null;
  score: number | null;
  course: { code: string; title: string } | null;
}

function toTrainingRecord(row: ApiTrainingRecord): TrainingRecord {
  return {
    id: row.id,
    employeeId: row.employeeId as TrainingRecord['employeeId'],
    employeeName: row.employeeName ?? '',
    courseId: row.courseId,
    courseTitle: row.course?.title ?? '',
    // The server has no session model; the prototype's field stays null rather
    // than inventing one.
    sessionId: null,
    status: row.status as TrainingRecord['status'],
    completedOn: row.completedOn as TrainingRecord['completedOn'],
    expiresOn: row.expiresOn as TrainingRecord['expiresOn'],
    certificateFileName: null,
    score: row.score,
  };
}

export const apiLearningService = {
  /**
   * Training records, read back at last.
   *
   * Enrolments were written through `POST /courses/:id/enrol` and never
   * listed, so this read honestly empty in API mode and "who still owes the
   * safety refresher" had no answer. The server derives expiry at read time,
   * so a lapsed certificate reports `expired` rather than the `completed` its
   * column still says.
   */
  async records(
    filter: PageRequest & { status?: string; courseId?: string } = {},
  ): Promise<Page<TrainingRecord>> {
    const query = new URLSearchParams();
    if (filter.courseId) query.set('courseId', filter.courseId);
    if (filter.status && filter.status !== 'all') query.set('status', filter.status);

    const suffix = query.size > 0 ? `?${query.toString()}` : '';
    const rows = (
      await apiTransport().get<ApiTrainingRecord[]>(`/api/v1/training-records${suffix}`)
    ).map(toTrainingRecord);

    const needle = filter.search?.trim().toLowerCase();
    const matched = needle
      ? rows.filter((row) =>
          `${row.employeeName} ${row.courseTitle}`.toLowerCase().includes(needle),
        )
      : rows;

    return {
      rows: matched,
      total: matched.length,
      page: filter.page ?? 1,
      pageSize: filter.pageSize ?? matched.length,
    };
  },

  /** Computed from the same rows the list shows, so the two cannot disagree. */
  async summary() {
    const rows = (await apiTransport().get<ApiTrainingRecord[]>('/api/v1/training-records')).map(
      toTrainingRecord,
    );
    const soon = Date.now() + 60 * 86_400_000;

    return {
      enrolled: rows.filter((row) => row.status === 'enrolled').length,
      completed: rows.filter((row) => row.status === 'completed').length,
      expiringSoon: rows.filter(
        (row) => row.expiresOn !== null && new Date(row.expiresOn).getTime() <= soon,
      ).length,
      mandatoryOutstanding: rows.filter((row) => row.status !== 'completed').length,
    };
  },

  async courses(filter: PageRequest = {}): Promise<Page<Course>> {
    const page = await apiTransport().page<ApiCourse>('/api/v1/courses');
    const needle = filter.search?.trim().toLowerCase();
    const rows = page.items
      .map(toCourse)
      .filter(
        (row) =>
          !needle || `${row.code} ${row.title} ${row.category}`.toLowerCase().includes(needle),
      );

    return {
      rows,
      total: rows.length,
      page: filter.page ?? 1,
      pageSize: filter.pageSize ?? rows.length,
    };
  },

  /**
   * Record a completion.
   *
   * The server derives the certificate's expiry from the course's validity, so
   * a certificate cannot outlive the rule that governs it — nothing here
   * computes a date. The route answers with the bare record: the API lists no
   * training records, so the employee and course names are honestly blank
   * rather than looked up from a surface that does not exist.
   */
  async markComplete(
    id: string,
    score: number | null,
    _actor: AuditActor,
  ): Promise<TrainingRecord> {
    const row = await apiTransport().post<ApiTrainingRecord>(
      `/api/v1/training-records/${id}/complete`,
      score === null ? {} : { score },
    );

    return {
      id: row.id,
      employeeId: row.employeeId,
      employeeName: '',
      courseId: row.courseId,
      courseTitle: '',
      sessionId: null,
      status: narrow(TRAINING_STATUSES, row.status, 'training status'),
      completedOn: row.completedOn ? row.completedOn.slice(0, 10) : null,
      expiresOn: row.expiresOn ? row.expiresOn.slice(0, 10) : null,
      certificateFileName: null,
      score: row.score,
    };
  },
};

interface ApiBenefitPlan {
  id: string;
  code: string;
  name: string;
  category: string | null;
  provider: string | null;
  employerMonthlyCost: string;
  employeeMonthlyCost: string;
  isActive: boolean;
  createdAt: string;
}

function toBenefitPlan(row: ApiBenefitPlan): BenefitPlan {
  return {
    id: row.id,
    name: row.name,
    // The server accepts any category string; the screen renders whatever
    // arrives and falls back to a neutral badge for one it has no colour for.
    // Substituting 'other' for an unrecognised value would relabel the plan.
    category: (row.category ?? 'other') as BenefitPlan['category'],
    provider: row.provider,
    description: '',
    employerContribution: toMinorUnits(row.employerMonthlyCost) ?? ZERO,
    employeeContribution: toMinorUnits(row.employeeMonthlyCost) ?? ZERO,
    // Dependent cover is not modelled on the API. False, so no enrolment form
    // invites dependent names the server has nowhere to keep.
    supportsDependents: false,
    active: row.isActive,
    createdAt: row.createdAt,
  };
}

/**
 * Benefit *plans* are what `/benefits` returns; an employee's membership of a
 * plan is a different record, read and written through `/benefit-enrollments`.
 * Presenting a plan as an enrolment would list every benefit the company
 * offers as though every employee held it, so the two stay apart here.
 */
interface ApiBenefitEnrollment {
  id: string;
  benefitPlanId: string;
  employeeId: string;
  status: string;
  enrolledOn: string;
  endedOn: string | null;
  createdAt: string;
  benefitPlan: { code: string; name: string; category: string } | null;
  employeeName?: string | null;
  employeeNumber?: string | null;
}

function toEnrollment(row: ApiBenefitEnrollment): BenefitEnrollmentRow {
  return {
    id: row.id,
    planId: row.benefitPlanId,
    planName: row.benefitPlan?.name ?? '',
    employeeId: row.employeeId as BenefitEnrollment['employeeId'],
    employeeName: row.employeeName ?? null,
    employeeNumber: row.employeeNumber ?? null,
    status: row.status as BenefitEnrollment['status'],
    // Date columns arrive as midnight instants; the screen wants the day.
    effectiveDate: row.enrolledOn.slice(0, 10) as BenefitEnrollment['effectiveDate'],
    endDate: (row.endedOn?.slice(0, 10) ?? null) as BenefitEnrollment['endDate'],
    dependentNames: [],
    documentName: null,
    notes: null,
    createdAt: row.createdAt as BenefitEnrollment['createdAt'],
  };
}

export const apiBenefitsService = {
  /**
   * Who is enrolled in what.
   *
   * The plan catalogue read from the API and the enrolments did not, so this
   * screen showed a real list of plans beside prototype membership.
   *
   * `dependentNames`, `documentName` and `notes` are absent from the server
   * model entirely; they come back empty rather than fabricated, and the
   * screens render them as unset.
   */
  async enrollmentsFor(employeeId: string): Promise<BenefitEnrollment[]> {
    const rows = await apiTransport().get<ApiBenefitEnrollment[]>(
      `/api/v1/benefit-enrollments?employeeId=${encodeURIComponent(employeeId)}`,
    );
    return rows.map(toEnrollment);
  },

  /** Every enrolment, or one plan's — who is on what, with names joined by the server. */
  async enrollments(filter: { planId?: string } = {}): Promise<BenefitEnrollmentRow[]> {
    const rows = await apiTransport().get<ApiBenefitEnrollment[]>('/api/v1/benefit-enrollments', {
      query: { benefitPlanId: filter.planId },
    });
    return rows.map(toEnrollment);
  },

  /**
   * POST /benefit-enrollments. The server refuses a second enrolment in the
   * same plan — including one that was ended, which it asks to be reactivated
   * rather than duplicated — and says which it was.
   */
  async enroll(
    planId: string,
    employeeId: string,
    effectiveDate: string,
    _actor: AuditActor,
  ): Promise<BenefitEnrollment> {
    const created = await apiTransport().post<ApiBenefitEnrollment>(
      '/api/v1/benefit-enrollments',
      { benefitPlanId: planId, employeeId, enrolledOn: effectiveDate },
      { idempotencyKey: newIdempotencyKey() },
    );
    const [enrollment] = await apiBenefitsService.enrollments({ planId });
    // The create answers without the plan joined; re-read so the caller gets
    // the plan's name (the newest enrolment in the plan is this one).
    return enrollment?.id === created.id
      ? enrollment
      : toEnrollment({ ...created, benefitPlan: null });
  },

  async plans(filter: PageRequest = {}): Promise<Page<BenefitPlan>> {
    const page = await apiTransport().page<ApiBenefitPlan>('/api/v1/benefits');
    const needle = filter.search?.trim().toLowerCase();
    const rows = page.items
      .map(toBenefitPlan)
      .filter((row) => !needle || `${row.name} ${row.category}`.toLowerCase().includes(needle));

    return {
      rows,
      total: rows.length,
      page: filter.page ?? 1,
      pageSize: filter.pageSize ?? rows.length,
    };
  },
};

interface ApiLoan {
  id: string;
  referenceCode: string;
  employeeId: string;
  employee?: { fullName: string; employeeNumber: string } | null;
  loanType: string;
  principal: string;
  totalPayable: string;
  amountPaid: string;
  installmentAmount: string;
  installmentsTotal: number;
  installmentsPaid: number;
  startPeriod: string | null;
  notes: string | null;
  status: string;
  payrollIntegrated: boolean;
  createdAt: string;
  /** Each period whose results deduct this loan, and whether it is posted. */
  repayments?: {
    periodId: string;
    periodLabel: string;
    periodStart?: string;
    periodStatus: string;
    amount: string;
    postedAt: string | null;
  }[];
}

/**
 * A loan as the register shows it: the shared record, plus the instalments
 * that sit on a period not (or no longer) posted — a period still being
 * prepared, or one reopened, which takes its instalment back off the loan.
 */
export type RegisterLoan = EmployeeLoan & {
  pendingPayments: {
    periodId: string;
    periodLabel: string;
    periodStart?: string;
    periodStatus: string;
    amount: Money;
  }[];
};

/**
 * Decimal string to minor units, without a float in the middle.
 *
 * Same reasoning as compensation: splitting on the point and padding keeps the
 * exact centavo where multiplying by 100 rounds the wrong way on values like
 * 12345.675 — and this figure is what somebody owes.
 */
function toMinorUnits(value: string | null): Money | null {
  if (value === null) return null;
  const [whole, fraction = ''] = value.split('.');
  const centavos = `${fraction}00`.slice(0, 2);
  const sign = whole.startsWith('-') ? -1 : 1;
  return { minorUnits: sign * (Math.abs(Number(whole)) * 100 + Number(centavos)), currency: 'PHP' };
}

const ZERO: Money = { minorUnits: 0, currency: 'PHP' };

function toLoan(row: ApiLoan): RegisterLoan {
  const repayments = row.repayments ?? [];
  return {
    id: row.id,
    referenceCode: row.referenceCode,
    employeeId: row.employeeId,
    employeeName: row.employee?.fullName,
    employeeNumber: row.employee?.employeeNumber,
    loanType: row.loanType,
    principal: toMinorUnits(row.principal) ?? ZERO,
    /*
     * Interest is not modelled on the API. The rate is not derived from
     * principal against payable here, because the difference between them may
     * be fees, insurance or interest, and presenting it as a rate would state
     * something the record does not say.
     */
    interestRatePercent: 0,
    totalPayable: toMinorUnits(row.totalPayable) ?? ZERO,
    /*
     * Kept on the loan and moved by payroll: each period's instalment is
     * posted to it when the period locks and taken back if it reopens. These
     * were hard-coded to zero, so a loan repaid in full read "0 of 12, ₱0.00".
     */
    amountPaid: toMinorUnits(row.amountPaid) ?? ZERO,
    installmentAmount: toMinorUnits(row.installmentAmount) ?? ZERO,
    installmentsTotal: row.installmentsTotal,
    installmentsPaid: row.installmentsPaid ?? 0,
    // "2026-06", the first period deducted from; empty until the loan starts.
    startPeriod: row.startPeriod ?? '',
    status: narrow(LOAN_STATUSES, row.status, 'loan status'),
    payrollIntegrated: row.payrollIntegrated,
    notes: row.notes ?? null,
    createdAt: row.createdAt,
    payments: repayments
      .filter((entry) => entry.postedAt !== null)
      .map((entry) => ({
        periodId: entry.periodId,
        periodLabel: entry.periodLabel,
        periodStart: entry.periodStart?.slice(0, 10),
        amount: toMinorUnits(entry.amount) ?? ZERO,
        postedAt: entry.postedAt!,
      })),
    pendingPayments: repayments
      .filter((entry) => entry.postedAt === null)
      .map((entry) => ({
        periodId: entry.periodId,
        periodLabel: entry.periodLabel,
        periodStart: entry.periodStart?.slice(0, 10),
        periodStatus: entry.periodStatus,
        amount: toMinorUnits(entry.amount) ?? ZERO,
      })),
  };
}

export const apiLoanService = {
  async list(
    filter: { status?: string; search?: string; page?: number; pageSize?: number } = {},
  ): Promise<Page<RegisterLoan>> {
    // The loans route parses no query — the status filter this adapter used
    // to send was silently ignored. The route caps itself at 300; filtering
    // and the search box happen here.
    const needle = filter.search?.trim().toLowerCase();
    const rows = (await apiTransport().get<ApiLoan[]>('/api/v1/loans'))
      .map(toLoan)
      .filter((row) => !filter.status || filter.status === 'all' || row.status === filter.status)
      .filter(
        (row) =>
          !needle ||
          `${row.referenceCode} ${row.loanType} ${row.employeeName ?? ''} ${row.employeeNumber ?? ''}`
            .toLowerCase()
            .includes(needle),
      );

    return {
      rows,
      total: rows.length,
      page: filter.page ?? 1,
      pageSize: filter.pageSize ?? rows.length,
    };
  },

  /** One employee's loans, filtered from the register — there is no per-employee route. */
  async forEmployee(employeeId: string): Promise<EmployeeLoan[]> {
    const rows = await apiTransport().get<ApiLoan[]>('/api/v1/loans');
    return rows.filter((row) => row.employeeId === employeeId).map(toLoan);
  },

  /**
   * The rail's numbers, counted from the same register the table renders.
   *
   * `outstanding` is what active loans still owe: total payable less what
   * payroll has already posted against each.
   */
  async summary() {
    const rows = (await apiTransport().get<ApiLoan[]>('/api/v1/loans')).map(toLoan);
    const active = rows.filter((row) => row.status === 'active');

    return {
      active: active.length,
      completed: rows.filter((row) => row.status === 'completed').length,
      outstanding: {
        minorUnits: active.reduce(
          (total, row) => total + (row.totalPayable.minorUnits - row.amountPaid.minorUnits),
          0,
        ),
        currency: 'PHP' as const,
      },
      notInPayroll: active.filter((row) => !row.payrollIntegrated).length,
    };
  },
};

/** What GET /employee-relations sends: the case row, its timeline, and who raised it. */
interface ApiRelationsCase {
  id: string;
  referenceCode: string;
  employeeId: string;
  kind: string;
  severity: string;
  subject: string;
  summary: string;
  /** Absent — not null — for a reader without employee_relations.view_confidential. */
  confidentialNotes?: string | null;
  status: string;
  raisedByUserId: string | null;
  raisedByName?: string | null;
  raisedAt: string;
  resolvedAt: string | null;
  outcome: string | null;
  events?: {
    id: string;
    kind: string;
    body: string;
    actorName: string | null;
    createdAt: string;
  }[];
}

/**
 * The employee names the case list needs but the endpoint does not send.
 *
 * `/employee-relations` deliberately returns bare rows — no employee join —
 * and a register keyed by uuid cannot be triaged. The directory answers the
 * question in one request. If the caller lacks `employees.view` the register
 * still renders, with the name column honestly blank, rather than the whole
 * module failing on a permission it does not strictly need.
 */
async function relationsNameIndex(): Promise<
  Map<string, { fullName: string; departmentName: string }>
> {
  try {
    const page = await apiTransport().page<{
      id: string;
      fullName: string;
      department: { id: string; name: string } | null;
    }>('/api/v1/employees', { query: { limit: 200 } });

    return new Map(
      page.items.map((row) => [
        row.id,
        { fullName: row.fullName, departmentName: row.department?.name ?? '' },
      ]),
    );
  } catch {
    return new Map();
  }
}

/**
 * Employee relations.
 *
 * Deliberately separate from the generic readers, so nobody adds a convenience
 * method to the shared factory and quietly gives this module a bulk export or a
 * search that crosses into it. The most restricted surface in the product
 * should be the one that is hardest to widen by accident.
 *
 * The list carries whatever the server chose to send: `confidentialNotes` is
 * absent rather than blanked for a caller without
 * `employee_relations.view_confidential`, and every read is audited with the
 * reader's address. Nothing here weakens either. Each case arrives with its
 * timeline, so an entry appended through the events route is read straight
 * back.
 */
export const apiEmployeeRelationsService = {
  async list(filter: PageRequest & { status?: string } = {}): Promise<Page<EmployeeRelationsCase>> {
    const [rows, names] = await Promise.all([
      apiTransport().get<ApiRelationsCase[]>('/api/v1/employee-relations'),
      relationsNameIndex(),
    ]);

    const needle = filter.search?.trim().toLowerCase();
    const mapped = rows
      .map((row): EmployeeRelationsCase => {
        const person = names.get(row.employeeId);
        return {
          id: row.id,
          referenceCode: row.referenceCode,
          employeeId: row.employeeId,
          employeeName: person?.fullName ?? '',
          departmentName: person?.departmentName ?? '',
          // The server validates severity as an enum and status against the
          // database's own; kind is a free string it renders as text. Casts,
          // not narrow() — an unrecognised kind is displayed, not fatal.
          kind: row.kind as EmployeeRelationsCase['kind'],
          severity: row.severity as EmployeeRelationsCase['severity'],
          status: row.status as EmployeeRelationsCase['status'],
          subject: row.subject,
          summary: row.summary,
          // Resolved by the server; blank rather than a uuid when it could not be.
          raisedByName: row.raisedByName ?? '',
          raisedAt: row.raisedAt,
          confidentialNotes: row.confidentialNotes ?? null,
          timeline: (row.events ?? []).map((event) => ({
            id: event.id,
            at: event.createdAt,
            actorName: event.actorName ?? '',
            kind: event.kind as EmployeeRelationsCase['timeline'][number]['kind'],
            body: event.body,
          })),
          outcome: row.outcome,
          closedAt: row.resolvedAt,
          attachmentNames: [],
        };
      })
      .filter((row) => !filter.status || filter.status === 'all' || row.status === filter.status)
      .filter(
        (row) =>
          !needle ||
          `${row.employeeName} ${row.referenceCode} ${row.subject}`.toLowerCase().includes(needle),
      );

    return {
      rows: mapped,
      total: mapped.length,
      page: filter.page ?? 1,
      pageSize: filter.pageSize ?? mapped.length,
    };
  },

  /**
   * One case, found in the audited list — the API has no GET
   * /employee-relations/:id, and the list read is the one whose access the
   * server records. The mock's client-side permission argument is not needed:
   * the server refuses a caller without `employee_relations.view`.
   */
  async get(id: string): Promise<EmployeeRelationsCase | null> {
    const page = await apiEmployeeRelationsService.list();
    return page.rows.find((row) => row.id === id) ?? null;
  },

  /**
   * Append a timeline entry.
   *
   * Behind `employee_relations.edit` server-side — the permission the screen
   * gates the form on; the actor and permission arguments exist to keep the
   * prototype's signature and are not sent. The
   * server's event vocabulary has no `status_change` — those are recorded by
   * status transitions, not typed in — and refuses one with its own message.
   */
  async addTimelineEntry(
    id: string,
    kind: EmployeeRelationsCase['timeline'][number]['kind'],
    body: string,
    _actor: AuditActor,
    _permissions: string[],
  ): Promise<EmployeeRelationsCase> {
    await apiTransport().post(`/api/v1/employee-relations/${id}/events`, { kind, body });

    const page = await apiEmployeeRelationsService.list();
    return found(
      page.rows.find((row) => row.id === id),
      'case',
    );
  },
};

/** GET /leave-types — the server's row. */
interface ApiLeaveType {
  id: string;
  code: string;
  name: string;
  defaultDaysPerYear: string;
  isPaid: boolean;
  requiresAttachment: boolean;
  isActive: boolean;
  /** On a create or an activation: how many employees were given the default credits. */
  balancesCreated?: number;
}

function toLeaveType(row: ApiLeaveType): LeaveType & { balancesCreated?: number } {
  return {
    ...(row.balancesCreated !== undefined ? { balancesCreated: row.balancesCreated } : {}),
    id: row.id,
    name: row.name,
    // The code is the short name: VL, SL — what balances and the phone show.
    shortName: row.code,
    paid: row.isPaid,
    defaultCredits: Number(row.defaultDaysPerYear),
    requiresAttachment: row.requiresAttachment,
    active: row.isActive,
  };
}

/** GET /leave-balances — one employee, one type, one year. */
interface ApiLeaveBalance {
  id: string;
  employeeId: string;
  leaveTypeId: string;
  year: number;
  entitled: string;
  used: string;
  pending: string;
  available: string;
  /** Manual adjustments inside `entitled`, summed from the ledger. */
  adjustments?: string;
  /** Adjustments dated after today — outside `entitled` and `available`. */
  scheduled?: string;
  scheduledFrom?: string | null;
  leaveType: { code: string; name: string } | null;
  employee: { employeeNumber: string; fullName: string } | null;
}

function toBalanceRecord(row: ApiLeaveBalance): LeaveBalanceRecord {
  const adjustments = Number(row.adjustments ?? 0);
  return {
    id: row.id,
    employeeId: row.employeeId,
    employeeName: row.employee?.fullName ?? '',
    leaveTypeId: row.leaveTypeId,
    leaveTypeName: row.leaveType?.name ?? '',
    // `entitled` is credits plus adjustments; the screen shows them apart.
    openingBalance: Number(row.entitled) - adjustments,
    used: Number(row.used),
    pending: Number(row.pending),
    adjustments,
    balance: Number(row.available),
    scheduled: Number(row.scheduled ?? 0),
    scheduledFrom: row.scheduledFrom ?? null,
    // Fetched when the adjustment drawer opens — see `history`.
    history: [],
  };
}

/**
 * Leave configuration against the API.
 *
 * Types, balances and adjustments all have routes: GET/POST/PATCH
 * /leave-types, GET /leave-balances, POST /leave-balances/:employeeId/
 * adjustments and the per-balance ledger. The balance list is the ledger's
 * result — `entitled`, `used`, `pending`, `available` — so nothing here
 * recomputes a figure the server already stands behind.
 */
export const apiLeaveConfigService = {
  async types(): Promise<LeaveType[]> {
    const rows = await apiTransport().get<ApiLeaveType[]>('/api/v1/leave-types');
    return rows.map(toLeaveType);
  },

  /**
   * Create or update a leave type.
   *
   * The server will not change a type's code or whether it is paid once it
   * exists — payroll reads both, and flipping `isPaid` would quietly change
   * what a past period recalculates to. Rather than send a change the server
   * ignores, that is refused here with the reason.
   */
  async saveType(
    input: LeaveType & { id?: string },
    _actor: AuditActor,
  ): Promise<LeaveType & { balancesCreated?: number }> {
    if (input.id) {
      const current = (await apiLeaveConfigService.types()).find((row) => row.id === input.id);
      if (current && (current.paid !== input.paid || current.shortName !== input.shortName)) {
        throw new Error(
          'The short name and whether a leave type is paid cannot change once it exists — payroll reads both. Make this type inactive and add a new one instead.',
        );
      }
      return toLeaveType(
        await apiTransport().patch<ApiLeaveType>(`/api/v1/leave-types/${input.id}`, {
          name: input.name.trim(),
          defaultDaysPerYear: input.defaultCredits,
          requiresAttachment: input.requiresAttachment,
          isActive: input.active,
        }),
      );
    }

    const code = input.shortName
      .trim()
      .toUpperCase()
      .replace(/[^A-Z0-9_]/g, '');
    if (!code) {
      throw new Error('Give the leave type a short name, such as VL — it is its code.');
    }
    const created = await apiTransport().post<ApiLeaveType>(
      '/api/v1/leave-types',
      {
        code,
        name: input.name.trim(),
        defaultDaysPerYear: input.defaultCredits,
        isPaid: input.paid,
        requiresAttachment: input.requiresAttachment,
        // Sent with the create: an active type is credited to everybody on
        // creation, so one added as inactive must never be active at all.
        isActive: input.active,
      },
      { idempotencyKey: newIdempotencyKey() },
    );
    return toLeaveType(created);
  },

  async balances(
    filter: PageRequest & { leaveTypeId?: string },
  ): Promise<Page<LeaveBalanceRecord>> {
    const rows = await apiTransport().get<ApiLeaveBalance[]>('/api/v1/leave-balances', {
      query: { leaveTypeId: filter.leaveTypeId },
    });
    const needle = filter.search?.trim().toLowerCase();
    const mapped = rows
      .map(toBalanceRecord)
      .filter(
        (row) =>
          !needle || `${row.employeeName} ${row.leaveTypeName}`.toLowerCase().includes(needle),
      );
    const pageSize = filter.pageSize ?? mapped.length;
    const pageNumber = filter.page ?? 1;
    return {
      rows: mapped.slice((pageNumber - 1) * pageSize, pageNumber * pageSize),
      total: mapped.length,
      page: pageNumber,
      pageSize,
    };
  },

  /**
   * One balance's ledger, newest first: every adjustment with its reason and
   * who made it, and the grants and leave taken beside them. Showing only a
   * count of adjustments ("Adjustments so far 1") answered none of the
   * questions an employee asks about their balance.
   */
  async history(record: LeaveBalanceRecord): Promise<LeaveBalanceRecord['history']> {
    const entries = await apiTransport().get<
      {
        id: string;
        reason: string;
        days: string;
        note: string;
        createdAt: string;
        effectiveDate?: string | null;
        actorName: string | null;
      }[]
    >(`/api/v1/leave-balances/${record.employeeId}/ledger`, {
      query: { leaveTypeId: record.leaveTypeId },
    });
    return entries.map((entry) => ({
      id: entry.id,
      at: entry.createdAt,
      actorName: entry.actorName ?? '',
      days: Number(entry.days),
      reason: entry.note,
      kind: entry.reason,
      // Adjustments made before the effective date was kept fall back to the
      // day they were recorded, rather than to a date nobody chose.
      effectiveDate: entry.effectiveDate
        ? entry.effectiveDate.slice(0, 10)
        : businessDateOf(entry.createdAt),
    }));
  },

  /**
   * POST /leave-balances/:employeeId/adjustments appends a ledger entry and
   * answers with the recomputed figures. The effective date is sent as
   * entered and kept on the entry; the server derives the ledger year from
   * it. Names come from the record the screen adjusted, since the ledger
   * answers with figures, not people.
   */
  async adjustBalance(
    input: {
      employeeId: string;
      leaveTypeId: string;
      days: number;
      reason: string;
      effectiveDate: string;
      employeeName?: string;
      leaveTypeName?: string;
    },
    _actor?: AuditActor,
  ): Promise<LeaveBalanceRecord> {
    const balance = await apiTransport().post<{
      entitled: string;
      used: string;
      pending: string;
      available: string;
      scheduled?: string;
      scheduledFrom?: string | null;
    }>(
      `/api/v1/leave-balances/${input.employeeId}/adjustments`,
      {
        leaveTypeId: input.leaveTypeId,
        // The date itself; the server takes the ledger year from it.
        effectiveDate: input.effectiveDate,
        days: input.days,
        reason: input.reason,
      },
      { idempotencyKey: newIdempotencyKey() },
    );

    return {
      id: `${input.employeeId}:${input.leaveTypeId}`,
      employeeId: input.employeeId,
      employeeName: input.employeeName ?? '',
      leaveTypeId: input.leaveTypeId,
      leaveTypeName: input.leaveTypeName ?? '',
      openingBalance: Number(balance.entitled),
      used: Number(balance.used),
      pending: Number(balance.pending),
      adjustments: input.days,
      balance: Number(balance.available),
      scheduled: Number(balance.scheduled ?? 0),
      scheduledFrom: balance.scheduledFrom ?? null,
      history: [],
    };
  },
};
